import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { HeartPulse, ShieldCheck, Activity, CheckCircle2, RotateCcw } from "lucide-react";
import BodyMap from "./BodyMap";
import { ZONE_LABELS, STRAIN_LEVELS, LEVEL_COLOR, ZONE_VIEW } from "./bodyMapConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PENALTY = { green: 0, yellow: 8, orange: 25, red: 45 };

const scoreOf = (values) =>
  Math.max(0, 100 - Object.values(values).reduce((s, l) => s + (PENALTY[l] || 0), 0));

const scoreTone = (s) => (s >= 85 ? "#22C55E" : s >= 60 ? "#EAB308" : s >= 40 ? "#F97316" : "#EF4444");

function ScoreRing({ score, accent }) {
  const r = 34, c = 2 * Math.PI * r;
  const tone = scoreTone(score);
  return (
    <div className="relative w-24 h-24" data-testid="readiness-score">
      <svg viewBox="0 0 80 80" className="w-24 h-24 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={tone} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * score) / 100}
          style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease", filter: `drop-shadow(0 0 3px ${tone}) drop-shadow(0 0 9px ${tone}88)` }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-cover text-2xl leading-none" style={{ color: tone, textShadow: `0 0 8px ${tone}aa` }}>{score}</span>
        <span className="text-[8px] uppercase tracking-widest text-white/50 mt-0.5">Ready</span>
      </div>
    </div>
  );
}

export default function ReadinessModule({ token }) {
  const [loading, setLoading] = useState(true);
  const [school, setSchool] = useState(null);
  const [values, setValues] = useState({});          // { part: level }
  const [recovery, setRecovery] = useState("");
  const [selected, setSelected] = useState(null);     // part currently being set
  const [view, setView] = useState("front");
  const [saving, setSaving] = useState(false);
  const [savedToday, setSavedToday] = useState(false);

  const accent = school?.primary_color || "#C41E24";
  const secondary = school?.secondary_color || "#D4AF37";
  const score = useMemo(() => scoreOf(values), [values]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/readiness/today`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        setSchool(data.school || null);
        if (data.checkin) {
          const v = {};
          (data.checkin.areas || []).forEach((a) => { v[a.part] = a.level; });
          setValues(v);
          setRecovery(data.checkin.recovery_log || "");
          setSavedToday(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const setLevel = (part, level) => {
    setValues((prev) => {
      const n = { ...prev };
      if (level === "clear") delete n[part]; else n[part] = level;
      return n;
    });
  };

  const submit = async () => {
    if (!recovery.trim()) { toast.error("Please log what you did for recovery yesterday."); return; }
    setSaving(true);
    try {
      const areas = Object.entries(values).map(([part, level]) => ({ part, level }));
      await axios.post(`${API}/readiness/checkin`, { areas, recovery_log: recovery.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      setSavedToday(true);
      toast.success(`Readiness logged — you're at ${score}%.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your check-in. Try again.");
    } finally { setSaving(false); }
  };

  if (loading) return null;

  const flagged = Object.entries(values).filter(([, l]) => l && l !== "green");

  return (
    <motion.section
      data-testid="readiness-module"
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mb-10 rounded-2xl overflow-hidden border relative"
      style={{ borderColor: `${accent}55`, background: "rgba(14,14,14,0.6)", backdropFilter: "blur(18px)",
               boxShadow: `0 0 30px ${accent}22` }}
    >
      {/* HUD header with dynamic school identity */}
      <div className="relative px-6 sm:px-8 py-6 border-b border-white/10"
           style={{ background: `linear-gradient(120deg, ${accent}26, transparent 65%)` }}>
        <div className="flex items-center gap-4">
          {school?.mascot_url ? (
            <img loading="lazy" decoding="async" src={`${process.env.REACT_APP_BACKEND_URL}${school.mascot_url}`} alt={school.name}
              data-testid="readiness-mascot"
              className="w-14 h-14 rounded-xl object-contain bg-black/40 border p-1.5 shrink-0"
              style={{ borderColor: `${secondary}66` }} />
          ) : (
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
              <ShieldCheck size={26} style={{ color: accent }} />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.28em]" style={{ color: secondary }}>
              High School Performance System · Readiness
            </div>
            <h2 className="font-headings text-2xl sm:text-3xl uppercase tracking-wide truncate">
              {school?.name || "Daily Readiness Check"}
            </h2>
          </div>
          <div className="ml-auto hidden sm:block"><ScoreRing score={score} accent={accent} /></div>
        </div>
      </div>

      <div className="px-6 sm:px-8 py-7 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        {/* Body map + level picker */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={16} style={{ color: accent }} />
            <h3 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80">Tap where you feel it</h3>
          </div>
          <p className="text-xs text-white/45 mb-4">Select a body part, then rate how it feels today.</p>

          <BodyMap mode="input" values={values} selectedPart={selected} onSelect={setSelected} accent={accent} view={view} onViewChange={setView} />

          {selected && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                data-testid="readiness-level-picker"
                className="mt-4 rounded-xl border border-white/10 bg-black/40 p-4">
                <div className="text-[11px] font-bold uppercase tracking-widest text-white/60 mb-3">
                  {ZONE_LABELS[selected]} — how does it feel?
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {STRAIN_LEVELS.map((lv) => {
                    const active = values[selected] === lv.key;
                    return (
                      <button key={lv.key}
                        data-testid={`readiness-level-${lv.key}`}
                        onClick={() => setLevel(selected, lv.key)}
                        className="flex flex-col items-start gap-1 rounded-lg px-3 py-2.5 text-left transition-all"
                        style={{ background: active ? lv.color : "rgba(255,255,255,0.04)",
                                 border: `1px solid ${active ? lv.color : "rgba(255,255,255,0.1)"}`,
                                 boxShadow: active ? `0 0 14px ${lv.color}66` : "none" }}>
                        <span className="w-3 h-3 rounded-full" style={{ background: active ? "#fff" : lv.color }} />
                        <span className={`text-[11px] font-bold uppercase tracking-wide ${active ? "text-black" : "text-white/85"}`}>{lv.label}</span>
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => { setLevel(selected, "clear"); setSelected(null); }}
                  className="mt-3 text-[11px] font-bold uppercase tracking-wider text-white/40 hover:text-white transition-colors">
                  Clear this area
                </button>
              </motion.div>
            )}

          {flagged.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="readiness-flagged-chips">
              {flagged.map(([part, lvl]) => (
                <button key={part} onClick={() => { setView(ZONE_VIEW[part]); setSelected(part); }}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide"
                  style={{ background: `${LEVEL_COLOR[lvl]}22`, border: `1px solid ${LEVEL_COLOR[lvl]}` }}>
                  <span className="w-2 h-2 rounded-full" style={{ background: LEVEL_COLOR[lvl] }} />
                  {ZONE_LABELS[part]}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recovery log + submit */}
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-2 mb-1">
            <HeartPulse size={16} style={{ color: accent }} />
            <h3 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/80">Recovery Log</h3>
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: accent }}>Required</span>
          </div>
          <p className="text-xs text-white/45 mb-3">What did you do for recovery yesterday? (Sleep, hydration, mobility)</p>
          <textarea
            data-testid="readiness-recovery-log"
            rows={6}
            value={recovery}
            onChange={(e) => setRecovery(e.target.value)}
            placeholder="e.g. 8h sleep, 2.5L water, 15 min foam rolling + hamstring mobility…"
            className="w-full flex-1 bg-black/40 border border-white/10 rounded-xl p-4 text-sm text-white resize-y focus-visible:outline-none focus-visible:ring-2"
            style={{ minHeight: 140 }}
            onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${accent}`)}
            onBlur={(e) => (e.target.style.boxShadow = "none")}
          />

          <div className="mt-4 flex items-center gap-3 sm:hidden">
            <ScoreRing score={score} accent={accent} />
            <div className="text-xs text-white/60">Today's readiness estimate</div>
          </div>

          <button
            data-testid="readiness-submit"
            onClick={submit}
            disabled={saving}
            className="mt-5 w-full flex items-center justify-center gap-2 font-subheadings font-bold uppercase tracking-wider py-3.5 rounded-sm transition-all disabled:opacity-70 text-white"
            style={{ background: accent, boxShadow: `0 0 20px ${accent}55` }}>
            {savedToday ? <RotateCcw size={16} /> : <CheckCircle2 size={16} />}
            {saving ? "Saving…" : savedToday ? "Update Today's Check-In" : "Submit Readiness Check-In"}
          </button>
          {savedToday && (
            <div data-testid="readiness-saved-badge" className="mt-3 flex items-center gap-2 text-xs text-[#22C55E]">
              <CheckCircle2 size={14} /> Logged for today — your coach can see it.
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}
